import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import api from '../../../api';
import imageCompression from 'browser-image-compression';
import TenantAdminLayout from '../../Layout/TenantAdminLayout';
import 'react-toastify/dist/ReactToastify.css';
import { 
  Upload, User, Home, GraduationCap, CheckCircle, ChevronRight, ChevronLeft,
  FileText, Eye, Trash2, AlertCircle, Plus, MapPin, 
  Shield, Info, X
} from 'lucide-react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

// ==========================================
// 1. CONSTANTS & DATA
// ==========================================

const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Jain', 'Buddhist', 'Parsi', 'Jewish'];
const MOTHER_TONGUES = ['Kannada', 'English', 'Hindi', 'Telugu', 'Tamil', 'Malayalam', 'Marathi', 'Urdu', 'Bengali', 'Gujarati'];
const NATIONALITIES = ['Indian', 'NRI', 'American', 'British', 'Canadian', 'Australian'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-'];

const MAX_EDUCATION = 6;
const EMPTY_EDU = {
  institutionName: '', previousClassGrade: '', yearOfPassing: '', 
  boardOrUniversity: '', registrationNumber: '', result: '', 
  mediumOfInstruction: '', transferCertificateNumber: '', 
  transferCertificateDate: '', maxMarks: '', obtainedMarks: '', educationProof: null
};

// ==========================================
// 2. UTILS
// ==========================================

const isValidPhoneNumber = (phone) => {
  if (!phone) return false;
  try {
    const phoneNumber = parsePhoneNumberFromString('+' + phone);
    return phoneNumber && phoneNumber.isValid();
  } catch {
    return false;
  }
};

const getCountryCode = (phone) => {
  if (!phone) return '';
  try {
    const phoneNumber = parsePhoneNumberFromString('+' + phone);
    return phoneNumber ? '+' + phoneNumber.countryCallingCode : '';
  } catch {
    return '';
  }
};

const getNationalNumber = (phone) => {
  if (!phone) return '';
  try {
    const phoneNumber = parsePhoneNumberFromString('+' + phone);
    return phoneNumber ? phoneNumber.nationalNumber : '';
  } catch {
    return '';
  }
};

// ==========================================
// 3. UI COMPONENTS
// ==========================================

const InputGroup = ({ icon: Icon, label, required, children, error }) => (
  <div className="space-y-1.5 w-full group">
    <label className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${error ? 'text-red-500' : 'text-gray-500 group-focus-within:text-blue-600'}`}>
      {Icon && <Icon className={`w-3.5 h-3.5 ${error ? 'text-red-400' : 'text-gray-400 group-focus-within:text-blue-500'}`} />}
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="relative">
      {children}
    </div>
    {error && <p className="text-xs text-red-500 mt-1 font-medium flex items-center gap-1 animate-pulse"><AlertCircle className="w-3 h-3"/>{error}</p>}
  </div>
);

const StyledSelect = ({ error, ...props }) => (
  <div className="relative">
    <select 
      className={`w-full pl-3 pr-8 py-2.5 bg-white border ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'} text-gray-800 text-sm rounded-lg focus:ring-4 outline-none transition-all appearance-none cursor-pointer shadow-sm`}
      {...props}
    />
    <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 rotate-90 pointer-events-none" />
  </div>
);

const StyledInput = ({ error, ...props }) => (
  <input 
    className={`w-full pl-3 pr-3 py-2.5 bg-white border ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-200' : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'} text-gray-800 text-sm rounded-lg focus:ring-4 outline-none transition-all placeholder:text-gray-400 shadow-sm disabled:bg-gray-50 disabled:text-gray-500`}
    {...props}
  />
);

const SelectWithOther = ({ label, name, value, options, onChange, required }) => {
  const isCustom = value && !options.includes(value);
  const [mode, setMode] = useState(isCustom ? 'custom' : 'select');

  const handleSelectChange = (e) => {
    if (e.target.value === 'OTHER_OPTION') {
      setMode('custom');
      onChange({ target: { name, value: '' } });
    } else {
      setMode('select');
      onChange(e);
    }
  };

  return (
    <InputGroup label={label} required={required}>
      {mode === 'select' ? (
        <StyledSelect name={name} value={value} onChange={handleSelectChange}>
          <option value="">Select {label}</option>
          {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          <option value="OTHER_OPTION" className="font-bold text-blue-600">+ Add Other</option>
        </StyledSelect>
      ) : (
        <div className="relative flex items-center gap-2 animate-fadeIn">
           <StyledInput name={name} value={value} onChange={onChange} placeholder={`Type ${label}...`} autoFocus />
           <button onClick={() => { setMode('select'); onChange({ target: { name, value: '' } }); }} className="p-2.5 bg-gray-100 hover:bg-red-50 text-gray-500 hover:text-red-500 rounded-lg transition-colors border border-gray-200" title="Back to list">
             <X className="w-4 h-4" />
           </button>
        </div>
      )}
    </InputGroup>
  );
};

const ToggleSwitch = ({ checked, onChange, label }) => (
  <label className="inline-flex items-center cursor-pointer gap-3 select-none group">
    <div className="relative">
      <input type="checkbox" className="sr-only peer" checked={checked} onChange={onChange} />
      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
    </div>
    <span className="text-sm font-semibold text-gray-600 group-hover:text-blue-600 transition-colors">{label}</span>
  </label>
);

const SectionHeader = ({ title, icon: Icon, description }) => (
  <div className="flex items-center gap-3 pb-4 mb-6 border-b border-gray-100">
    <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl shadow-sm border border-blue-100">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h3 className="text-lg font-bold text-gray-800 leading-none">{title}</h3>
      {description && <p className="text-xs text-gray-500 mt-1.5">{description}</p>}
    </div>
  </div>
);

const PreviewModal = ({ isOpen, onClose, fileUrl, fileType }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 animate-fadeIn" onClick={onClose}>
      <div className="bg-white rounded-2xl overflow-hidden shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col relative scale-100 transition-transform" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="font-bold text-gray-700 flex items-center gap-2"><Eye className="w-4 h-4"/> Document Preview</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors"><X className="w-5 h-5 text-gray-500"/></button>
        </div>
        <div className="flex-1 overflow-auto p-8 bg-gray-100/50 flex items-center justify-center">
          {fileType?.startsWith('image/') ? (
            <img src={fileUrl} alt="Preview" className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-md border border-gray-200" />
          ) : (
             <iframe src={fileUrl} title="PDF Preview" className="w-full h-[65vh] rounded-lg border border-gray-200 shadow-sm bg-white" />
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 4. MAIN PAGE COMPONENT
// ==========================================

const AddStudent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, academicYearId } = location.state || {};
  
  const topRef = useRef(null);

  // --- State ---
  const [academicYears, setAcademicYears] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Selections
  const [selectedAY, setSelectedAY] = useState(academicYearId || '');
  const [selectedProgramType, setSelectedProgramType] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedSemester, setSelectedSemester] = useState('');
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [preferredClass, setPreferredClass] = useState('');
  const [terms, setTerms] = useState([]);
  const token = location.state?.token || localStorage.getItem('token');
  
  // Form State
  const [form, setForm] = useState({
    firstName: '', lastName: '', gender: '', dateOfBirth: '', email: '',
    studentPhoneNumber: '', parentName: '', parentPhoneNumber: '', parentEmail: '', // ADDED parentEmail
    USN: '', admissionDate: ''
  });
  
  const [profile, setProfile] = useState({
    permanentAddress: { houseNoStreet: '', cityTownVillage: '', stateProvince: '', district: '', postalZipCode: '', country: '' },
    correspondenceAddress: { houseNoStreet: '', cityTownVillage: '', stateProvince: '', district: '', postalZipCode: '', country: '' },
    emergencyContactPerson: '', emergencyContactNumber: '', religion: '',
    motherTongue: '', nationality: '', domicileState: '', hasDisability: false,
    disabilityType: '', bloodGroup: '', category: '', admissionType: 'Regular',
    studentEntryType: 'Regular', otherDetails: {}
  });

  const [educationList, setEducationList] = useState([{ ...EMPTY_EDU }]);
  const [proofPreviews, setProofPreviews] = useState([]);
  const [copyAddress, setCopyAddress] = useState(false);
  const [autoUSN, setAutoUSN] = useState(true);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingClasses, setIsFetchingClasses] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentPreview, setCurrentPreview] = useState(null);

  const steps = [
    { id: 1, title: "Academic", icon: GraduationCap },
    { id: 2, title: "Personal", icon: User },
    { id: 3, title: "Address", icon: MapPin },
    { id: 4, title: "Education", icon: FileText },
    { id: 5, title: "Review", icon: CheckCircle }
  ];

  // --- Core Logic Helpers (Stable) ---
  const handleAYChange = useCallback(async (id, list = academicYears) => {
    setSelectedAY(id);
    setSelectedSemester('');
    setSelectedProgram('');
    setSelectedProgramType('');
    setAvailableClasses([]);
    setPreferredClass('');
    
    const ay = (list || []).find(y => y._id === id);
    if (ay) {
      setIsSemesterBased(ay.isSemesterBased || false);
      if (ay.isSemesterBased) {
        try {
          const res = await api.get(`/api/classManager/semesters/${id}`);
          setSemesters(res.data.semesters || []);
        } catch (e) { setSemesters([]); }
      } else {
        setSemesters([]);
        try {
          const termRes = await api.get(`/api/academic-year/${id}/terms?tenantId=${tenantId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setTerms(termRes.data.terms || []);
        } catch (e) {
          setTerms([]);
        }
      }
    }
  }, [academicYears, tenantId, token]);

  // --- Effects ---

  // 1. Initial Data Load
  useEffect(() => {
    if (!tenantId) { toast.error("Tenant ID required"); navigate(-1); return; }

    const loadData = async () => {
      try {
        setLoading(true);
        const [ayRes, ptRes] = await Promise.all([
          api.get(`/api/academic-year/all-active?tenantId=${tenantId}`),
          api.get(`/api/program-types?tenantId=${tenantId}&status=active`)
        ]);
        const fetchedYears = ayRes.data.academicYears || [];
        setAcademicYears(fetchedYears);
        setProgramTypes(ptRes.data.programTypes || ptRes.data || []);

        if (academicYearId && fetchedYears.length > 0) {
          setSelectedAY(academicYearId);
        } else if (fetchedYears.length > 0) {
          setSelectedAY(fetchedYears[0]._id);
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load initial data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantId, academicYearId, navigate]); 

  useEffect(() => {
    if (selectedAY && academicYears.length > 0) {
      handleAYChange(selectedAY, academicYears);
    }
  }, [selectedAY, academicYears, handleAYChange]);

  // 2. Fetch Programs
  useEffect(() => {
    if (!selectedProgramType) { setPrograms([]); setSelectedProgram(''); return; }
    const fetchPrograms = async () => {
      try {
        const res = await api.get(`/api/programs?tenantId=${tenantId}&programTypeId=${selectedProgramType}&status=active`);
        setPrograms(res.data.programs || []);
      } catch (e) { console.error(e); }
    };
    fetchPrograms();
  }, [selectedProgramType, tenantId]);

  // 3. Fetch Classes
  useEffect(() => {
    const fetchClasses = async () => {
      if (!selectedAY) { setAvailableClasses([]); return; }
      try {
        setIsFetchingClasses(true);
        let url = '';
        if (isSemesterBased) {
          if (!selectedSemester) { setAvailableClasses([]); return; }
          url = `/api/classManager/classes-for-semester/${selectedSemester}`;
        } else {
          if (profile.term) {
            url = `/api/classManager/classes-for-term/${profile.term}`;
          } else {
            setAvailableClasses([]); return;
          }
        }
        const res = await api.get(url);
        setAvailableClasses(res.data.classes || res.data.annualBasedClasses || []);
      } catch (e) { setAvailableClasses([]); } finally { setIsFetchingClasses(false); }
    };
    const timer = setTimeout(fetchClasses, 300);
    return () => clearTimeout(timer);
  }, [selectedAY, selectedSemester, isSemesterBased, profile.term]);

  // 4. Address Sync
  useEffect(() => {
    if (copyAddress) {
      setProfile(prev => ({ ...prev, correspondenceAddress: { ...prev.permanentAddress } }));
    }
  }, [copyAddress, profile.permanentAddress]);

  useEffect(() => { topRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [step]);

  // --- Handlers ---

  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handlePhoneInput = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleProfileInput = (e) => {
    const { name, value, type, checked } = e.target;
    setProfile(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleAddressInput = (type, field, value) => {
    setProfile(prev => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  const handlePhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.1, maxWidthOrHeight: 1024, useWebWorker: true });
      const newFile = new File([compressed], file.name, { type: file.type });
      setPhotoFile(newFile);
      setPhotoPreview(URL.createObjectURL(newFile));
    } catch (e) { toast.error("Image Error"); }
  };

  const handleEduChange = (idx, field, value) => {
    setEducationList(list => { const u = [...list]; u[idx][field] = value; return u; });
  };

  const handleEduProof = (idx, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setProofPreviews(prev => { 
        const n = [...prev]; 
        n[idx] = { url: reader.result, type: file.type, name: file.name, size: file.size }; 
        return n; 
      });
    };
    reader.readAsDataURL(file);
    setEducationList(list => { const u = [...list]; u[idx].educationProof = file; return u; });
  };

  const openPreview = (idx) => {
    if (proofPreviews[idx]) {
      setCurrentPreview(proofPreviews[idx]);
      setModalOpen(true);
    }
  };

  // --- Validation ---
  const validateStep = (currentStep) => {
    const newErrors = {};
    let isValid = true;

    if (currentStep === 1) {
      if (!selectedAY) { newErrors.academicYear = "Required"; isValid = false; }
      if (!selectedProgram) { newErrors.program = "Required"; isValid = false; }
      if (!isSemesterBased && !profile.term) { newErrors.term = "Required"; isValid = false; }
    }
    if (currentStep === 2) {
      if (!form.firstName) { newErrors.firstName = "Required"; isValid = false; }
      if (!form.studentPhoneNumber || !isValidPhoneNumber(form.studentPhoneNumber)) {
        newErrors.studentPhoneNumber = "Invalid phone number"; isValid = false;
      }
      if (!form.parentName) { newErrors.parentName = "Required"; isValid = false; }
      if (!form.parentPhoneNumber || !isValidPhoneNumber(form.parentPhoneNumber)) {
        newErrors.parentPhoneNumber = "Invalid phone number"; isValid = false;
      }
      if (form.parentEmail && !form.parentEmail.includes('@')) {
        newErrors.parentEmail = "Invalid email format"; isValid = false;
      }
      if (!profile.bloodGroup) { newErrors.bloodGroup = "Required"; isValid = false; }
    }
    if (currentStep === 3) {
      if (!profile.emergencyContactPerson) newErrors.emergencyPerson = "Required";
      if (!profile.emergencyContactNumber || !isValidPhoneNumber(profile.emergencyContactNumber)) {
        newErrors.emergencyNumber = "Invalid phone number"; isValid = false;
      }
      if (!profile.permanentAddress.houseNoStreet) newErrors.houseNo = "Required";
    }
    setErrors(newErrors);
    if (!isValid) toast.error("Please fill mandatory fields", { icon: <AlertCircle className="text-red-500"/> });
    return isValid;
  };

  // --- Handlers ---
  
  const handleNext = () => {
    if (validateStep(step)) setStep(s => s + 1);
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = new FormData();
      Object.keys(form).forEach(k => {
        let v = form[k];
        if (v) payload.append(k, Array.isArray(v) ? v[0] : v);
      });
      if (autoUSN) payload.set('USN', '');

      payload.append('tenantId', tenantId);
      payload.append('academicYearId', selectedAY);
      payload.append('programId', selectedProgram);
      if (isSemesterBased && selectedSemester) payload.append('semesterId', selectedSemester);
      if (preferredClass) payload.append('preferredClassName', preferredClass);
      if (photoFile) payload.append('profilePhoto', photoFile);
      if (!isSemesterBased && profile.term) payload.append('termId', profile.term);

      Object.entries(profile.permanentAddress).forEach(([key, value]) => payload.append(`permanentAddress[${key}]`, value));
      Object.entries(profile.correspondenceAddress).forEach(([key, value]) => payload.append(`correspondenceAddress[${key}]`, value));
      payload.append('sameAsPermanent', copyAddress);

      const res = await api.post('/api/students/add', payload, { headers: { 'Content-Type': 'multipart/form-data' } });
      const sid = res.data.student?._id || res.data.studentId;

      const profilePayload = {
        ...profile,
        studentId: sid,
        tenantId,
        academicYearId: selectedAY,
        phoneNumber: { number: getNationalNumber(form.studentPhoneNumber), countryCode: getCountryCode(form.studentPhoneNumber) },
        parentPhoneNumber: { number: getNationalNumber(form.parentPhoneNumber), countryCode: getCountryCode(form.parentPhoneNumber) },
        emergencyContactNumber: { number: getNationalNumber(profile.emergencyContactNumber), countryCode: getCountryCode(profile.emergencyContactNumber) },
        address: profile.permanentAddress.houseNoStreet,
        admissionNumber: res.data.student?.admissionNumber || '',
        admissionDate: form.admissionDate || '',
        previousEducation: educationList.map(({educationProof, ...rest}) => rest),
        preferredMediumOfInstruction: profile.preferredMediumOfInstruction || 'English',
        profileCompletion: true
      };
      
      delete profilePayload.verificationStatus;

      await api.post(`/api/students/profile/${sid}`, profilePayload);

      for (let idx = 0; idx < educationList.length; idx++) {
        if (educationList[idx].educationProof) {
          const fd = new FormData();
          fd.append('educationProof', educationList[idx].educationProof);
          await api.post(`/api/files/student/${sid}/education/${idx}/proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }

      toast.success("Student Admitted Successfully!");
      setTimeout(() => navigate(-1), 1500);
    } catch (err) {
      toast.error(err.response?.data?.message || "Admission Failed");
    } finally { setIsSubmitting(false); }
  };

  // --- Sub-Renderers ---

  const renderAcademicStep = () => (
    <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm animate-fadeIn">
      <SectionHeader title="Academic Context" icon={GraduationCap} description="Select the academic year and program for the new admission." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <InputGroup label="Academic Year" required error={errors.academicYear}>
          <StyledSelect value={selectedAY} onChange={e => handleAYChange(e.target.value)}>
            <option value="">Select Year</option>
            {academicYears.map(y => <option key={y._id} value={y._id}>{y.name}</option>)}
          </StyledSelect>
        </InputGroup>
        <InputGroup label="Program Type" required error={errors.programType}>
          <StyledSelect value={selectedProgramType} onChange={e => setSelectedProgramType(e.target.value)}>
            <option value="">Select Type</option>
            {programTypes.map(pt => <option key={pt._id} value={pt._id}>{pt.name}</option>)}
          </StyledSelect>
        </InputGroup>
        <InputGroup label="Program" required error={errors.program}>
          <StyledSelect value={selectedProgram} onChange={e => setSelectedProgram(e.target.value)} disabled={!selectedProgramType}>
            <option value="">Select Program</option>
            {programs.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </StyledSelect>
        </InputGroup>
        {isSemesterBased && (
          <InputGroup label="Semester" required error={errors.semester}>
            <StyledSelect value={selectedSemester} onChange={e => setSelectedSemester(e.target.value)}>
              <option value="">Select Semester</option>
              {semesters.map(s => <option key={s._id} value={s._id}>{s.semesterName}</option>)}
            </StyledSelect>
          </InputGroup>
        )}
        {!isSemesterBased && (
          <InputGroup label="Term" required error={errors.term}>
            <StyledSelect value={profile.term || ''} onChange={e => setProfile(prev => ({ ...prev, term: e.target.value }))}>
              <option value="">Select Term</option>
              {terms.map(t => (
                <option key={t._id} value={t._id}>{t.termLabel}</option>
              ))}
            </StyledSelect>
          </InputGroup>
        )}
        <InputGroup label="Admission Type">
          <StyledSelect name="admissionType" value={profile.admissionType} onChange={handleProfileInput}>
            <option value="Regular">Regular</option>
            <option value="Lateral">Lateral Entry</option>
            <option value="Management Quota">Management Quota</option>
            <option value="NRI">NRI</option>
          </StyledSelect>
        </InputGroup>
        <InputGroup label="Class Assignment" icon={Home}>
          {/* Debug: Show classes fetched for term-based selection */}
          {!isSemesterBased && profile.term && (
            <>
              {console.log('Classes fetched for selected term (termId=' + profile.term + '):', availableClasses)}
              {availableClasses.length === 0 && (
                <div className="text-xs text-red-500 mb-2">No classes found for the selected term. Please check if classes are created for this term.</div>
              )}
            </>
          )}
          <StyledSelect value={preferredClass} onChange={e => setPreferredClass(e.target.value)} disabled={isFetchingClasses}>
            <option value="">Pool (Unassigned)</option>
            {availableClasses.map(c => <option key={c._id} value={c.className}>{c.className}</option>)}
          </StyledSelect>
        </InputGroup>
      </div>
    </div>
  );

  const renderPersonalStep = () => (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-fadeIn">
      <div className="lg:col-span-3 space-y-6">
        <div className="bg-white p-6 rounded-2xl border border-gray-200 text-center shadow-sm">
          <div className="w-32 h-32 mx-auto rounded-full bg-gray-50 flex items-center justify-center border-4 border-white shadow-lg relative group overflow-hidden mb-4">
            {photoPreview ? <img src={photoPreview} className="w-full h-full object-cover" alt="Pre"/> : <User className="w-12 h-12 text-gray-300"/>}
            <label className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity backdrop-blur-[1px]">
              <div className="flex flex-col items-center text-white">
                <Upload className="w-6 h-6 mb-1"/>
                <span className="text-[10px] font-bold uppercase">Upload</span>
              </div>
              <input type="file" className="hidden" accept="image/*" onChange={handlePhoto} />
            </label>
          </div>
          <p className="text-xs text-gray-400 font-medium">Supported: JPG, PNG</p>
        </div>
        
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">USN ID</span>
            <ToggleSwitch checked={autoUSN} onChange={e => setAutoUSN(e.target.checked)} label={autoUSN ? "Auto" : "Manual"} />
          </div>
          {!autoUSN && <StyledInput name="USN" value={form.USN} onChange={handleInput} placeholder="Enter USN ID" />}
        </div>
      </div>

      <div className="lg:col-span-9 space-y-6">
        <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
          <SectionHeader title="Basic Info" icon={User} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputGroup label="First Name" required error={errors.firstName}> <StyledInput name="firstName" value={form.firstName} onChange={handleInput} /> </InputGroup>
            <InputGroup label="Last Name" required> <StyledInput name="lastName" value={form.lastName} onChange={handleInput} /> </InputGroup>
            <InputGroup label="Email" required> <StyledInput type="email" name="email" value={form.email} onChange={handleInput} /> </InputGroup>
            <InputGroup label="Phone" required error={errors.studentPhoneNumber}>
              <PhoneInput
                country={'in'}
                value={form.studentPhoneNumber}
                onChange={v => handlePhoneInput('studentPhoneNumber', v)}
                inputClass="w-full pl-12 pr-3 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-100 text-sm"
              />
            </InputGroup>
            <InputGroup label="Gender"> 
              <StyledSelect name="gender" value={form.gender} onChange={handleInput}>
                <option value="">Select</option><option value="male">Male</option><option value="female">Female</option>
              </StyledSelect>
            </InputGroup>
            <InputGroup label="DOB"> <StyledInput type="date" name="dateOfBirth" value={form.dateOfBirth} onChange={handleInput} /> </InputGroup>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
          <SectionHeader title="Demographics" icon={Info} />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <SelectWithOther label="Religion" name="religion" value={profile.religion} options={RELIGIONS} onChange={handleProfileInput} />
            <SelectWithOther label="Mother Tongue" name="motherTongue" value={profile.motherTongue} options={MOTHER_TONGUES} onChange={handleProfileInput} />
            <InputGroup label="Category"> 
              <StyledSelect name="category" value={profile.category} onChange={handleProfileInput}>
                <option value="">Select</option><option value="General">General</option><option value="OBC">OBC</option><option value="SC">SC</option><option value="ST">ST</option>
              </StyledSelect>
            </InputGroup>
            <SelectWithOther label="Nationality" name="nationality" value={profile.nationality} options={NATIONALITIES} onChange={handleProfileInput} />
            <SelectWithOther label="Blood Group" name="bloodGroup" value={profile.bloodGroup} options={BLOOD_GROUPS} onChange={handleProfileInput} required error={errors.bloodGroup} />
          </div>
        </div>
        
        <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
           <SectionHeader title="Parent Details" icon={Shield} />
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <InputGroup label="Parent Name" required error={errors.parentName}> 
               <StyledInput name="parentName" value={form.parentName} onChange={handleInput} /> 
             </InputGroup>
             <InputGroup label="Parent Phone" required error={errors.parentPhoneNumber}>
               <PhoneInput
                 country={'in'}
                 value={form.parentPhoneNumber}
                 onChange={v => handlePhoneInput('parentPhoneNumber', v)}
                 inputClass="w-full pl-12 pr-3 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-100 text-sm"
               />
             </InputGroup>
             {/* ADDED Parent Email Field */}
             <InputGroup label="Parent Email" error={errors.parentEmail}>
               <StyledInput 
                 type="email" 
                 name="parentEmail" 
                 value={form.parentEmail || ''} 
                 onChange={handleInput} 
                 placeholder="parent@example.com" 
               />
             </InputGroup>
           </div>
        </div>
      </div>
    </div>
  );

  const renderAddressStep = () => (
    <div className="space-y-6 animate-fadeIn">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
          <SectionHeader title="Permanent Address" icon={Home} />
          <div className="space-y-4">
            <InputGroup label="Street" required error={errors.houseNo}> <StyledInput value={profile.permanentAddress.houseNoStreet} onChange={e => handleAddressInput('permanentAddress', 'houseNoStreet', e.target.value)} /> </InputGroup>
            <div className="grid grid-cols-2 gap-4">
              <InputGroup label="City"> <StyledInput value={profile.permanentAddress.cityTownVillage} onChange={e => handleAddressInput('permanentAddress', 'cityTownVillage', e.target.value)} /> </InputGroup>
              <InputGroup label="District"> <StyledInput value={profile.permanentAddress.district} onChange={e => handleAddressInput('permanentAddress', 'district', e.target.value)} /> </InputGroup>
              <InputGroup label="State"> <StyledInput value={profile.permanentAddress.stateProvince} onChange={e => handleAddressInput('permanentAddress', 'stateProvince', e.target.value)} /> </InputGroup>
              <InputGroup label="Zip"> <StyledInput value={profile.permanentAddress.postalZipCode} onChange={e => handleAddressInput('permanentAddress', 'postalZipCode', e.target.value)} /> </InputGroup>
              <InputGroup label="Country"> <StyledInput value={profile.permanentAddress.country} onChange={e => handleAddressInput('permanentAddress', 'country', e.target.value)} /> </InputGroup>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm">
          <div className="flex justify-between border-b border-gray-100 pb-4 mb-4">
            <div className="flex gap-2 items-center"><MapPin className="w-5 h-5 text-blue-600"/><h3 className="font-bold text-gray-800">Current Address</h3></div>
            <ToggleSwitch checked={copyAddress} onChange={e => setCopyAddress(e.target.checked)} label="Same as Permanent" />
          </div>
          <div className={`space-y-4 ${copyAddress ? 'opacity-50 pointer-events-none' : ''}`}>
            <InputGroup label="Street"> <StyledInput value={profile.correspondenceAddress.houseNoStreet} onChange={e => handleAddressInput('correspondenceAddress', 'houseNoStreet', e.target.value)} /> </InputGroup>
            <div className="grid grid-cols-2 gap-4">
              <InputGroup label="City"> <StyledInput value={profile.correspondenceAddress.cityTownVillage} onChange={e => handleAddressInput('correspondenceAddress', 'cityTownVillage', e.target.value)} /> </InputGroup>
              <InputGroup label="State"> <StyledInput value={profile.correspondenceAddress.stateProvince} onChange={e => handleAddressInput('correspondenceAddress', 'stateProvince', e.target.value)} /> </InputGroup>
              <InputGroup label="Zip"> <StyledInput value={profile.correspondenceAddress.postalZipCode} onChange={e => handleAddressInput('correspondenceAddress', 'postalZipCode', e.target.value)} /> </InputGroup>
              <InputGroup label="Country"> <StyledInput value={profile.correspondenceAddress.country} onChange={e => handleAddressInput('correspondenceAddress', 'country', e.target.value)} /> </InputGroup>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-red-50 p-8 rounded-2xl border border-red-100">
        <SectionHeader title="Emergency Contact" icon={AlertCircle} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <InputGroup label="Person Name" required error={errors.emergencyPerson}> <StyledInput value={profile.emergencyContactPerson} onChange={e => setProfile(p=>({...p, emergencyContactPerson: e.target.value}))} /> </InputGroup>
          <InputGroup label="Phone Number" required error={errors.emergencyNumber}>
            <PhoneInput
              country={'in'}
              value={profile.emergencyContactNumber || ''}
              onChange={phone => setProfile(p => ({ ...p, emergencyContactNumber: phone }))}
              inputProps={{ name: 'emergencyContactNumber', required: true }}
              inputClass="w-full pl-12 pr-3 py-2.5 bg-white border border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-100 text-sm"
            />
          </InputGroup>
        </div>
      </div>
    </div>
  );

  const renderEducationStep = () => (
    <div className="space-y-5 animate-fadeIn">
      {educationList.map((edu, idx) => (
        <div key={idx} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-4">
            <span className="text-sm font-bold text-gray-700">Record #{idx+1}</span>
            {educationList.length>1 && <button onClick={()=>{ setEducationList(l=>l.filter((_,i)=>i!==idx)) }} className="text-red-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4"/></button>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-4">
            <InputGroup label="Institution"> <StyledInput value={edu.institutionName} onChange={e=>handleEduChange(idx, 'institutionName', e.target.value)} /> </InputGroup>
            <InputGroup label="Grade/Class"> <StyledInput value={edu.previousClassGrade} onChange={e=>handleEduChange(idx, 'previousClassGrade', e.target.value)} /> </InputGroup>
            <InputGroup label="Year"> <StyledInput value={edu.yearOfPassing} onChange={e=>handleEduChange(idx, 'yearOfPassing', e.target.value)} /> </InputGroup>
            <InputGroup label="Board"> <StyledInput value={edu.boardOrUniversity} onChange={e=>handleEduChange(idx, 'boardOrUniversity', e.target.value)} /> </InputGroup>
            <InputGroup label="Score"> <StyledInput value={edu.result} onChange={e=>handleEduChange(idx, 'result', e.target.value)} /> </InputGroup>
          </div>
          <div className="border-t border-gray-100 pt-4 flex items-center gap-4">
            {!proofPreviews[idx] ? (
              <label className="flex items-center gap-3 cursor-pointer p-2 hover:bg-gray-50 rounded-lg transition-colors border border-transparent hover:border-gray-200">
                <div className="bg-blue-50 p-2 rounded text-blue-600"><Upload className="w-4 h-4"/></div>
                <span className="text-xs text-blue-600 font-bold">Upload Proof</span>
                <input type="file" className="hidden" accept="image/*,application/pdf" onChange={e=>handleEduProof(idx, e.target.files[0])}/>
              </label>
            ) : (
              <div className="flex items-center gap-3">
                 <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 px-3 py-1.5 rounded-lg border border-green-100">
                    <CheckCircle className="w-4 h-4"/> 
                    <span className="truncate max-w-[150px]">{proofPreviews[idx].name}</span>
                 </div>
                 <button 
                    onClick={() => openPreview(idx)}
                    className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Preview Document"
                 >
                    <Eye className="w-4 h-4" />
                 </button>
              </div>
            )}
          </div>
        </div>
      ))}
      {educationList.length < MAX_EDUCATION && <button onClick={()=>{ setEducationList(l=>[...l, {...EMPTY_EDU}]) }} className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-400 hover:border-blue-400 hover:text-blue-500 font-bold flex justify-center items-center gap-2 transition-colors"><Plus className="w-5 h-5"/> Add Qualification</button>}
    </div>
  );

  const renderReviewStep = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 animate-fadeIn overflow-hidden">
      <div className="p-10 text-center border-b border-gray-100 bg-gray-50/50">
         <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
           <FileText className="w-8 h-8" />
         </div>
         <h2 className="text-2xl font-extrabold text-gray-900">Review Application</h2>
         <p className="text-gray-500 mt-2 max-w-md mx-auto">Please verify all student details carefully before final submission. This action will create a new user record.</p>
      </div>
      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-8 bg-white">
          <div className="space-y-6">
             <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
                <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2 uppercase tracking-wide text-xs"><User className="w-4 h-4"/> Personal Details</h4>
                <div className="grid grid-cols-2 gap-y-4 gap-x-2">
                   {[
                     ['Full Name', `${form.firstName} ${form.lastName}`],
                     ['Email', form.email],
                     ['Phone', form.studentPhoneNumber],
                     ['DOB', form.dateOfBirth],
                     ['Parent', `${form.parentName} (${form.parentPhoneNumber})`],
                     ['Parent Email', form.parentEmail || '-'],
                     ['Category', profile.category]
                   ].map(([l,v]) => (
                     <div key={l}><p className="text-[10px] font-bold text-gray-400 uppercase">{l}</p><p className="text-sm font-semibold text-gray-800 break-words">{v || '-'}</p></div>
                   ))}
                </div>
             </div>
          </div>
          <div className="space-y-6">
             <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200">
                <h4 className="font-bold text-gray-700 mb-4 flex items-center gap-2 uppercase tracking-wide text-xs"><GraduationCap className="w-4 h-4"/> Academic & Address</h4>
                <div className="grid grid-cols-2 gap-y-4 gap-x-2">
                  {[
                     ['Year', academicYears.find(y=>y._id===selectedAY)?.name],
                     ['Program', programs.find(p=>p._id===selectedProgram)?.name],
                     ['Class', preferredClass || 'Pool'],
                     ['Address', `${profile.permanentAddress.cityTownVillage}, ${profile.permanentAddress.stateProvince}`]
                   ].map(([l,v]) => (
                     <div key={l}><p className="text-[10px] font-bold text-gray-400 uppercase">{l}</p><p className="text-sm font-semibold text-gray-800 break-words">{v || '-'}</p></div>
                   ))}
                </div>
             </div>
          </div>
      </div>
    </div>
  );

  // Loading Screen
  if (loading) return (
    <TenantAdminLayout>
      <div className="flex flex-col h-[80vh] justify-center items-center gap-4">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"/>
        <p className="text-gray-500 font-medium animate-pulse">Loading Context...</p>
      </div>
    </TenantAdminLayout>
  );

  return (
    <TenantAdminLayout>
      <div className="min-h-screen bg-gray-50/40 pb-24" ref={topRef}>
        <ToastContainer position="top-right" autoClose={3000} />
        <PreviewModal isOpen={modalOpen} onClose={() => setModalOpen(false)} fileUrl={currentPreview?.url} fileType={currentPreview?.type} />
        
        <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-sm transition-all">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div>
                <h1 className="text-xl font-extrabold text-gray-900 tracking-tight">New Admission</h1>
                <p className="text-xs text-gray-500 font-medium">Create a new student record</p>
              </div>
              
              <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-xl">
                {steps.map((s) => (
                  <button 
                    key={s.id}
                    onClick={() => {
                        if (s.id < step || validateStep(step)) setStep(s.id);
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold transition-all ${step === s.id ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] ${step === s.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>{s.id}</span>
                    <span className="hidden md:inline">{s.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-8">
           <div className="min-h-[500px]">
              {step === 1 && renderAcademicStep()}
              {step === 2 && renderPersonalStep()}
              {step === 3 && renderAddressStep()}
              {step === 4 && renderEducationStep()}
              {step === 5 && renderReviewStep()}
           </div>

           <div className="flex justify-between items-center mt-12 pt-6 border-t border-gray-200">
             {step > 1 ? (
               <button onClick={()=>setStep(s=>s-1)} className="flex items-center gap-2 px-6 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-bold text-sm hover:bg-gray-50 transition-colors">
                 <ChevronLeft className="w-4 h-4"/> Back
               </button>
             ) : (
               <button onClick={()=>navigate(-1)} className="px-6 py-2.5 rounded-xl text-gray-500 font-bold text-sm hover:bg-gray-100 transition-colors">Cancel</button>
             )}
             
             {step === 5 ? (
               <button 
                 onClick={handleFinalSubmit} 
                 disabled={isSubmitting} 
                 className="flex items-center gap-2 px-8 py-2.5 bg-green-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-green-200 hover:bg-green-700 hover:-translate-y-0.5 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
               >
                 {isSubmitting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> Processing...</> : <>Confirm Admission <CheckCircle className="w-4 h-4"/></>}
               </button>
             ) : (
               <button 
                 onClick={handleNext} 
                 className="flex items-center gap-2 px-8 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-sm shadow-lg shadow-blue-200 hover:bg-blue-700 hover:-translate-y-0.5 transition-all"
               >
                 Next Step <ChevronRight className="w-4 h-4"/>
               </button>
             )}
           </div>
        </div>
      </div>
    </TenantAdminLayout>
  );
};

export default AddStudent;